import { Box, Button, Paper, Skeleton, Typography } from '@mui/material'
import { AssignmentLateOutlined as AssignmentLateIcon } from '@mui/icons-material'
import { tabularNums } from '../../theme'
import type { AssignmentSummaryCounts } from '../../utils/contactAssignment'

interface AssignmentSummaryProps {
  counts: AssignmentSummaryCounts
  clientLabel: string
  loading?: boolean
  /** Narrows the table to the contacts behind the "not reached out" count. */
  onShowAwaiting: () => void
  /** True while that narrowing is already applied, so the link does not offer it again. */
  awaitingFilterActive?: boolean
}

const Figure = ({ value, color = 'text.primary' }: { value: number; color?: string }) => (
  <Box component="span" sx={{ ...tabularNums, fontWeight: 600, color }}>
    {value}
  </Box>
)

const Dot = () => (
  <Box component="span" aria-hidden sx={{ color: 'divider' }}>
    ·
  </Box>
)

/**
 * MWA-354 — the coach's accountability line for one client: how many contacts
 * were assigned, how many the client has not reached out to, and how many of
 * those have waited past the overdue threshold. Staff only; the client portal
 * does not render it.
 *
 * Derived from the contact list the page already loaded, so it cannot disagree
 * with the table under it, and a failed load is the page's error state rather
 * than a summary of zero.
 */
export default function AssignmentSummary({
  counts,
  clientLabel,
  loading = false,
  onShowAwaiting,
  awaitingFilterActive = false,
}: AssignmentSummaryProps) {
  if (loading) {
    return <Skeleton variant="rounded" height={48} sx={{ mb: 2, borderRadius: '10px' }} />
  }

  // Nothing assigned is a starting point, not a failure, so it reads as an
  // instruction rather than a row of zeros.
  if (counts.assigned === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        No contacts have been assigned to {clientLabel} yet. Set an “Assigned on” date on a contact
        to track whether they reach out.
      </Typography>
    )
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        mb: 2,
        borderRadius: '10px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 1,
        rowGap: 0.5,
      }}
    >
      <Typography
        variant="body2"
        component="p"
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.75, rowGap: 0.25, m: 0 }}
      >
        <Figure value={counts.assigned} /> assigned
        <Dot />
        {counts.awaiting === 0 ? (
          <span>all reached out</span>
        ) : (
          <>
            <Figure value={counts.awaiting} /> not reached out
          </>
        )}
        {counts.overdue > 0 && (
          <>
            <Dot />
            {/* One inline run, so the flex gap cannot open a space before the comma. */}
            <Box component="span">
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.dark' }}>
                <AssignmentLateIcon sx={{ fontSize: 16, alignSelf: 'center' }} aria-hidden />
                <Figure value={counts.overdue} color="warning.dark" /> overdue
              </Box>
              {counts.oldestAwaitingDays != null && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  , oldest <Box component="span" sx={tabularNums}>{counts.oldestAwaitingDays}</Box>{' '}
                  {counts.oldestAwaitingDays === 1 ? 'day' : 'days'}
                </Box>
              )}
            </Box>
          </>
        )}
      </Typography>
      {counts.awaiting > 0 && !awaitingFilterActive && (
        <Button size="small" onClick={onShowAwaiting} sx={{ ml: { sm: 'auto' }, minHeight: { xs: 44, sm: 36 } }}>
          Show who hasn’t been reached
        </Button>
      )}
    </Paper>
  )
}
